import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, Outlet } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import '@aws-amplify/ui-react/styles.css';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomerProvider } from './contexts/CustomerContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import CustomerSelector from './components/CustomerSelector';
import QuestionnaireList from './components/QuestionnaireList';
import Questionnaire from './components/Questionnaire';

// Survey - Public (no auth)
import SurveyPublicPage from './components/survey/SurveyPublicPage';
import SurveyCompletePage from './components/survey/SurveyCompletePage';
import SurveyExpiredPage from './components/survey/SurveyExpiredPage';

// Survey - Admin
import SurveyDashboard from './components/admin/SurveyDashboard';
import SurveyCreate from './components/admin/SurveyCreate';
import SurveyDetail from './components/admin/SurveyDetail';
import SurveyResults from './components/admin/SurveyResults';
import SurveyCompare from './components/admin/SurveyCompare';
import DimensionsMapPage from './components/admin/DimensionsMapPage';
import TemplateManagement from './components/admin/TemplateManagement';
import TemplateUpload from './components/admin/TemplateUpload';
import TemplateEditor from './components/admin/TemplateEditor';
import EmailGroupManagement from './components/admin/EmailGroupManagement';
import CustomerManagement from './components/admin/CustomerManagement';

// Config
import { amplifyConfig } from './config/amplify-config';

// Configure Amplify
Amplify.configure(amplifyConfig);

/**
 * Layout route that provides authentication + main layout shell.
 * Uses react-router v6 Outlet pattern for nested routes.
 */
const AuthenticatedLayout: React.FC = () => {
  return (
    <AuthProvider>
      <CustomerProvider>
        <ProtectedRoute>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </ProtectedRoute>
      </CustomerProvider>
    </AuthProvider>
  );
};

const LanguageSwitch: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  return (
    <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)' }}>
      <button
        onClick={() => setLanguage('ja')}
        style={{
          padding: '4px 8px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: language === 'ja' ? '#61dafb' : 'transparent',
          color: language === 'ja' ? '#282c34' : 'rgba(255,255,255,0.7)',
        }}
      >
        JP
      </button>
      <button
        onClick={() => setLanguage('en')}
        style={{
          padding: '4px 8px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: language === 'en' ? '#61dafb' : 'transparent',
          color: language === 'en' ? '#282c34' : 'rgba(255,255,255,0.7)',
        }}
      >
        EN
      </button>
    </div>
  );
};

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { signOut, user } = useAuth();
  const { t } = useLanguage();

  return (
    <div>
      <header style={{
        backgroundColor: '#282c34',
        padding: '16px 20px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Link to="/admin/customers" style={{ textDecoration: 'none', color: 'white' }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{t('header', 'title')}</h1>
          </Link>
          <nav style={{ display: 'flex', gap: '12px' }}>
            <Link to="/admin/customers" style={{ color: '#a0d2f7', textDecoration: 'none', fontSize: '0.9rem', padding: '4px 8px' }}>
              {t('header', 'customers')}
            </Link>
            <Link to="/admin/surveys" style={{ color: '#a0d2f7', textDecoration: 'none', fontSize: '0.9rem', padding: '4px 8px' }}>
              {t('header', 'surveys')}
            </Link>
            <Link to="/admin/templates" style={{ color: '#a0d2f7', textDecoration: 'none', fontSize: '0.9rem', padding: '4px 8px' }}>
              {t('header', 'templates')}
            </Link>
            <Link to="/admin/email-groups" style={{ color: '#a0d2f7', textDecoration: 'none', fontSize: '0.9rem', padding: '4px 8px' }}>
              {t('header', 'emailGroups')}
            </Link>
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <span style={{ fontSize: '0.9rem' }}>
            {user?.email || user?.username || t('header', 'user')}
          </span>
          <LanguageSwitch />
          <button
            onClick={signOut}
            style={{
              padding: '8px 16px',
              backgroundColor: '#61dafb',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {t('header', 'logout')}
          </button>
        </div>
      </header>
      <main style={{ minHeight: 'calc(100vh - 80px)', backgroundColor: '#f5f5f5' }}>
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
    <Router>
      <Routes>
        {/* Public survey routes - NO auth required */}
        <Route path="/survey/:token" element={<SurveyPublicPage />} />
        <Route path="/survey/:token/complete" element={<SurveyCompletePage />} />
        <Route path="/survey/:token/expired" element={<SurveyExpiredPage />} />

        {/* Protected routes - uses layout route with Outlet */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<Navigate to="/admin/customers" replace />} />
          <Route path="/dashboard" element={<CustomerSelector />} />
          <Route path="/customer/:customerId" element={<QuestionnaireList />} />
          <Route path="/customer/:customerId/questionnaire/:questionnaireId" element={<Questionnaire />} />
          <Route path="/customer/:customerId/response/:responseId" element={<Questionnaire />} />

          {/* Admin Survey routes */}
          <Route path="/admin/surveys" element={<SurveyDashboard />} />
          <Route path="/admin/surveys/create" element={<SurveyCreate />} />
          <Route path="/admin/surveys/:surveyId" element={<SurveyDetail />} />
          <Route path="/admin/surveys/:surveyId/results" element={<SurveyResults />} />
          <Route path="/admin/surveys/:surveyId/compare" element={<SurveyCompare />} />
          <Route path="/admin/surveys/:surveyId/dimensions" element={<DimensionsMapPage />} />

          {/* Admin Template routes */}
          <Route path="/admin/templates" element={<TemplateManagement />} />
          <Route path="/admin/templates/upload" element={<TemplateUpload />} />
          <Route path="/admin/templates/:templateId/edit" element={<TemplateEditor />} />

          {/* Admin Email Group routes */}
          <Route path="/admin/email-groups" element={<EmailGroupManagement />} />

          {/* Admin Customer Management routes */}
          <Route path="/admin/customers" element={<CustomerManagement />} />

          <Route path="*" element={<Navigate to="/admin/customers" replace />} />
        </Route>
      </Routes>
    </Router>
    </LanguageProvider>
  );
}

export default App;
